/**
 * Ripia メインアプリケーション
 * HITOYASUMIプロダクション対応 - 認証統合版
 *
 * @description 認証状態に基づくメインコンテンツ切り替え
 * @authentication JWT + 暗号化Token管理
 * @security 業界最高水準セキュリティ
 */

import React, { useEffect } from 'react';
import {
  QueryClient,
  QueryClientProvider,
  keepPreviousData,
} from '@tanstack/react-query';
import { ClerkProvider } from '@clerk/clerk-react';
import { jaJP } from '@clerk/localizations';
import { ThemeProvider } from 'next-themes';
import { BrowserRouter as Router } from 'react-router-dom';
import AppRouter from './AppRouter';
import SessionManager from './components/auth/SessionManager';
import { AuthProvider, useAuthContext } from './hooks/useAuth';
import { PermissionProvider } from './hooks/usePermissions';
import { ErrorProvider } from './contexts/ErrorContext';
import { MockClerkProvider } from './mocks/MockClerkProvider';
import './styles/globals.css';
import { InitialDataService } from './services/initialDataService';
import logger from './utils/logger';
import {
  initOneSignal,
  setOneSignalUser,
  isOneSignalConfigured,
} from './lib/onesignal';
import { runLegacyCleanup } from './utils/legacyCleanup';
import {
  cleanupLegacyStorageData,
  setupCrossTabSync,
} from './utils/storageUtils';
import { GlobalLoadingIndicator } from './components/ui/GlobalLoadingIndicator';
import { initializeDeviceId } from './utils/crypto';
import { isBookingHost } from './utils/bookingHost';
import { RouteErrorBoundary } from './components/ErrorBoundary';

// QueryClient インスタンスを作成（App関数の外側）
// Issue #2839 C-1: テストから `placeholderData: keepPreviousData` の設定を
// 直接 assert できるよう named export する (frontend/src/__tests__/queryClient.config.test.ts)。
// eslint-disable-next-line react-refresh/only-export-components
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute
      retry: 1,
      // Issue #2839: SWR主軸ローディングUX — 再フェッチ時に前データを残しつつ
      // 裏で静かに更新する。GlobalLoadingIndicator が裏更新を可視化する。
      placeholderData: keepPreviousData,
    },
  },
});

/**
 * 認証状態に基づくメインコンテンツ表示
 */
const AppContent: React.FC = () => {
  const { isAuthenticated, isInitializing, user } = useAuthContext();

  // 統合されたデータ初期化（認証成功後に実行）
  // Note: customers/reservations loading removed - TanStack Query auto-loads on page mount (Issue #1282)
  useEffect(() => {
    if (!isAuthenticated) return;

    const initializeAppData = async () => {
      try {
        // Legacy data cleanup (Issue #1049 Phase 4)
        runLegacyCleanup();
        // Issue #1149: TTL形式でないlocalStorageデータを一括クリーンアップ
        cleanupLegacyStorageData();

        // 統一フラグで初期化済みかチェック
        const isInitialized = localStorage.getItem('RIPIA_COMPLETE_SETUP_v2');

        if (isInitialized !== 'true') {
          // Ripiaデータ初期化処理（本番環境では詳細ログを無効化）

          // 初期デモデータのセットアップ
          await InitialDataService.setupInitialData();

          // 統一完了フラグを設定
          localStorage.setItem('RIPIA_COMPLETE_SETUP_v2', 'true');
          // Ripiaデータ初期化完了（本番環境では詳細ログを無効化）
        } else {
          // Ripiaデータ初期化済み（本番環境では詳細ログを無効化）
        }

        // 開発環境でInitialDataServiceをグローバルに利用可能にする
        if (import.meta.env.DEV) {
          (
            window as typeof window & {
              InitialDataService: typeof InitialDataService;
            }
          ).InitialDataService = InitialDataService;

          // 開発用: LocalStorageリセット機能
          (
            window as typeof window & { resetDemoData: () => void }
          ).resetDemoData = () => {
            InitialDataService.resetDemoData();
            localStorage.removeItem('hasInitialData');
            localStorage.removeItem('RIPIA_COMPLETE_SETUP_v2');
            window.location.reload();
          };
          logger.log(
            '📱 開発用コマンド: window.resetDemoData() でデモデータをリセット'
          );
          // ユーザー情報は本番環境では非表示（セキュリティ保護）
        }

        // OneSignal Push通知初期化（Phase 0: 技術検証）
        if (isOneSignalConfigured()) {
          try {
            const initialized = await initOneSignal();
            if (initialized && user?.id) {
              await setOneSignalUser(user.id);
              logger.log('🔔 OneSignal initialized with user:', user.id);
            }
          } catch (oneSignalError) {
            logger.warn(
              'OneSignal initialization failed (non-critical):',
              oneSignalError
            );
          }
        }
      } catch (error) {
        logger.error('❌ App data initialization error:', error);
        // エラーが発生してもアプリケーションは続行
      }
    };

    initializeAppData();
  }, [isAuthenticated, user]);

  // Issue #3066: 認証初期化中は何も表示しない (spinner 撲滅方針)
  if (isInitializing) {
    return null;
  }

  // 認証状態に基づく表示切り替え（構造を統一）
  // 注: エラー表示は以下で処理:
  //   - 認証ページ(Login/Register): ページ内でインライン表示
  //   - 認証済みページ: SessionManagerで表示
  return (
    <>
      <SessionManager />
      <PermissionProvider>
        <AppRouter />
      </PermissionProvider>
    </>
  );
};

/**
 * Issue #2838 Phase 2: 公開予約サイト (booking.ripia.jp) 専用ツリー。
 *
 * `ClerkProvider` を一切マウントしない独立した React ツリーを返す。これにより
 * Clerk SDK の script ロード状況 (成功/失敗/タイムアウト) に関係なく、booking
 * サブドメインは常に公開予約サイトとして起動する。これが #2838 Phase 2 の核心。
 *
 * ## 意図的に除外する Provider (最小構成)
 *
 * `booking` 配下で `useQuery` / `useTheme` / `useAuth` / `useErrorContext` の
 * 使用有無を grep で確認した結果、これらは全て 0 件。不要な依存は導入しない
 * 原則に従い、以下を除外する:
 *
 * - `ClerkProvider` / `MockClerkProvider` / `AuthProvider` / `PermissionProvider`
 *   / `SessionManager`: **本修正の核心**。booking に認証コンポーネントは不要。
 * - `QueryClientProvider`: booking は `axios` を直接使い、tanstack/react-query
 *   を参照しない。現時点では不要。将来 booking 側で tanstack 導入の判断が出た
 *   際はここに追加する。
 * - `ThemeProvider` (next-themes): booking は `--booking-primary` CSS 変数で
 *   テーマカラーを管理しており、`useTheme` / `dark:` class を使わない。
 * - `GlobalLoadingIndicator`: `useIsFetching` が `QueryClientProvider` 依存で
 *   あり、かつ tanstack 未使用のため無意味。
 *
 * ## 意図的にスキップする初期化処理
 *
 * `ManagementApp` 側の `initializeDeviceId` / `setupCrossTabSync` は booking
 * では不要 (Device ID は Clerk 認証に紐付く IndexedDB 操作、cross-tab sync は
 * localStorage migration 用)。`App` dispatcher の `isBookingHost()` 分岐で
 * 物理的にスキップされる (hook ルール上、booking 経路には `useEffect` を
 * 配置できない構造になっている)。
 *
 * @see Issue #2838
 */
const BookingApp: React.FC = () => {
  return (
    <ErrorProvider>
      {/*
        `RouteErrorBoundary` を最外層で wrap する defense-in-depth。
        本来 `AppRouter` 内部の booking 分岐 (`AppRouter.tsx` L250) にも
        `RouteErrorBoundary` があるが、万が一 `BrowserRouter` や `AppRouter`
        自体が初期 render で throw した場合 (lazy chunk load error 等)、
        内部の boundary には到達しない。`ErrorProvider` の global handler は
        React render 中の error を捕捉できないため、render-time の白画面
        防止として独立した boundary が必要。
      */}
      <RouteErrorBoundary>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <AppRouter />
        </Router>
      </RouteErrorBoundary>
    </ErrorProvider>
  );
};

// Issue #1665: Declare __CLERK_KEY__ which is injected by Vite's define config
declare const __CLERK_KEY__: string | undefined;

/**
 * 管理画面ツリー (app.ripia.jp / localhost / preview) — Issue #2838 Phase 2
 *
 * `App` からこのコンポーネントへ管理画面用のロジックを分離している。理由は
 * React Hooks ルール (`react-hooks/rules-of-hooks`): `App` の冒頭で
 * `if (isBookingHost()) return <BookingApp />;` と早期 return する場合、
 * その後に置かれる `useEffect` が条件付き呼び出しと判定される。`App` を
 * 純粋な dispatcher (hook 呼び出しゼロ) に分離し、管理画面用の `useEffect`
 * (Device ID 初期化・cross-tab sync) を本コンポーネントに閉じ込めることで、
 * booking 経路は管理画面側の `useEffect` を構造的に一切経由しなくなる。
 */
const ManagementApp: React.FC = () => {
  // Issue #1665: Use __CLERK_KEY__ (from Vite define) if available, otherwise fallback to import.meta.env
  // This bypasses Vite's import.meta.env handling which may load wrong .env file
  const envKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
  const defineKey = typeof __CLERK_KEY__ !== 'undefined' ? __CLERK_KEY__ : '';
  // Prefer define key (from process.env) over env file key
  const clerkPubKey = defineKey || envKey;
  const enableDevLogin = import.meta.env.VITE_ENABLE_DEV_LOGIN;
  const mode = import.meta.env.MODE;

  // Issue #1665 / #3075: Debug - log which Publishable Key is loaded in browser
  // production build (Vercel Preview / Prod) では Publishable Key 先頭 30 文字を
  // DevTools に出さない。import.meta.env.DEV のみ有効化
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.log(
      '[App.tsx] __CLERK_KEY__ (from define):',
      defineKey?.substring(0, 30) || 'not set'
    );
    // eslint-disable-next-line no-console
    console.log(
      '[App.tsx] import.meta.env key:',
      envKey?.substring(0, 30) || 'not set'
    );
    // eslint-disable-next-line no-console
    console.log(
      '[App.tsx] Final clerkPubKey:',
      clerkPubKey?.substring(0, 30) || 'not set'
    );
    // eslint-disable-next-line no-console
    console.log('[App.tsx] MODE:', mode);
  }

  // WT2引き継ぎ: Device ID初期化とクロスタブ同期（Issue #1278, #1277）
  useEffect(() => {
    let crossTabCleanup: (() => void) | undefined;

    const initializeApp = async () => {
      try {
        // Device ID初期化（IndexedDB永続化）
        const deviceId = await initializeDeviceId();
        logger.info(
          '[App] Device ID initialized:',
          deviceId.substring(0, 8) + '...'
        );

        // クロスタブ同期リスナー設定
        crossTabCleanup = setupCrossTabSync((event) => {
          // マイグレーション関連のイベントを処理
          if (event.key?.includes('migration')) {
            logger.debug(
              '[App] Cross-tab migration event detected:',
              event.key
            );
          }
        });
        logger.debug('[App] Cross-tab sync listener registered');
      } catch (error) {
        logger.error('[App] App initialization failed:', error);
      }
    };

    initializeApp();

    // クリーンアップ
    return () => {
      if (crossTabCleanup) {
        crossTabCleanup();
      }
    };
  }, []);

  // 開発環境でDEV_LOGINが有効な場合はClerkを完全にスキップ
  const skipClerk = mode === 'development' && enableDevLogin === 'true';

  if (!clerkPubKey && !skipClerk) {
    console.error('❌ VITE_CLERK_PUBLISHABLE_KEY is not set');
  }

  if (skipClerk) {
    console.log(
      '🔧 [DEV] Bypassing Clerk for screenshot mode - using PreviewAuthService'
    );
  }

  // ClerkProviderまたはMockClerkProviderを選択（型安全性のため条件分岐を明示）
  if (skipClerk) {
    return (
      <QueryClientProvider client={queryClient}>
        <GlobalLoadingIndicator />
        <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
          <MockClerkProvider>
            <ErrorProvider>
              <AuthProvider>
                <Router
                  future={{
                    v7_startTransition: true,
                    v7_relativeSplatPath: true,
                  }}
                >
                  <AppContent />
                </Router>
              </AuthProvider>
            </ErrorProvider>
          </MockClerkProvider>
        </ThemeProvider>
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <GlobalLoadingIndicator />
      <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
        <ClerkProvider
          publishableKey={clerkPubKey || ''}
          proxyUrl={import.meta.env.VITE_CLERK_PROXY_URL || undefined}
          localization={jaJP}
          signUpFallbackRedirectUrl="/dashboard" /* Issue #1459: Dashboard-First */
          signInFallbackRedirectUrl="/dashboard"
        >
          <ErrorProvider>
            <AuthProvider>
              <Router
                future={{
                  v7_startTransition: true,
                  v7_relativeSplatPath: true,
                }}
              >
                <AppContent />
              </Router>
            </AuthProvider>
          </ErrorProvider>
        </ClerkProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

/**
 * Issue #2838 Phase 2: アプリケーションのルートディスパッチャ。
 *
 * `window.location.hostname` を見て、公開予約サイト (`booking.ripia.jp`) と
 * 管理画面 (`app.ripia.jp` / `localhost` / preview) のどちらのツリーを
 * マウントするかを決定する **純粋な dispatcher**。React Hooks を一切
 * 呼び出さないため、`react-hooks/rules-of-hooks` に違反せずに早期 return
 * できる。
 *
 * ⚠️ 重要: 本関数に `useEffect` / `useState` 等の hook を追加してはならない。
 * 追加したい場合は、対象のツリー (`BookingApp` または `ManagementApp`) 側に
 * 追加すること。ここに hook を入れると、booking 経路で Clerk 関連 state に
 * 巻き込まれる可能性が復活し、#2838 の元凶が再発する。
 */
const App: React.FC = () => {
  if (isBookingHost()) {
    return <BookingApp />;
  }
  return <ManagementApp />;
};

export default App;
